import { DataProvider } from 'react-admin';
import tokenManager from '../tokenManager';

const downloadCampaignFile = async (guid: string, endpoint: string, filename: string) => {
    let token = tokenManager.getToken();
    if (!token) {
        const refreshed = await tokenManager.getRefreshedToken();
        if (!refreshed) throw new Error('Kirjautuminen on vanhentunut.');
        token = tokenManager.getToken();
    }
    const response = await fetch(
        `${process.env.REACT_APP_INFRA_API}/campaign/${encodeURIComponent(guid)}/${endpoint}`,
        {
            headers: { Authorization: `Bearer ${token}` },
        }
    );
    if (!response.ok) {
        const message = await response.text();
        throw new Error(message || `CSV-vienti epäonnistui (${response.status}).`);
    }
    const blob = await response.blob();
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    link.remove();
    URL.revokeObjectURL(url);
};

const addInfraMethods = (dataProvider: DataProvider, httpClient) => {
    return {
        ...dataProvider,
        cancelUserMerge: async (guid: string) => {
            await httpClient(`${process.env.REACT_APP_INFRA_API}/user/merge/${encodeURIComponent(guid)}/cancel`, {
                method: 'POST',
            });
            return { data: { id: guid } };
        },

        getAny: async (resource, params) => {
            //let query = (params != undefined && params != '') ? `?${stringify(params)}` : '';
            const query =
                params !== undefined && params !== '' ? `?${params}` : '';

            const url = `${process.env.REACT_APP_INFRA_API}/${resource}${query}`;
            //console.log('getAny:' + url);

            const { json } = await httpClient(url);
            return { data: json };
        },

        emailCampaignExport: async (guid: string) => {
            await httpClient(
                `${process.env.REACT_APP_INFRA_API}/campaign/${encodeURIComponent(guid)}/export/email`,
                { method: 'POST' }
            );
        },

        downloadCampaignCsv: async (guid: string) => {
            await downloadCampaignFile(
                guid,
                'export',
                `sample-books-${guid}.csv`
            );
        },

        downloadCampaignResponsesCsv: async (guid: string) => {
            await downloadCampaignFile(
                guid,
                'responses/export',
                `sample-books-responses-${guid}.csv`
            );
        },

        getTestScenarios: async () => {
            const url = `${process.env.REACT_APP_INFRA_API}/admin/test-scenarios`;
            const { json } = await httpClient(url);
            return { data: json };
        },

        createTestScenario: async (data: any) => {
            const url = `${process.env.REACT_APP_INFRA_API}/admin/test-scenarios`;
            const { json } = await httpClient(url, {
                method: 'POST',
                body: JSON.stringify(data),
            });
            return { data: json };
        },

        deleteTestScenario: async (name: string) => {
            const url = `${process.env.REACT_APP_INFRA_API}/admin/test-scenarios/${encodeURIComponent(name)}`;
            const { json } = await httpClient(url, {
                method: 'DELETE',
            });
            return { data: json };
        },

        persistTestScenario: async (name: string) => {
            const url = `${process.env.REACT_APP_INFRA_API}/admin/test-scenarios/${encodeURIComponent(name)}/persist`;
            const { json } = await httpClient(url, {
                method: 'POST',
            });
            return { data: json };
        },

        unpersistTestScenario: async (name: string) => {
            const url = `${process.env.REACT_APP_INFRA_API}/admin/test-scenarios/${encodeURIComponent(name)}/persist`;
            const { json } = await httpClient(url, {
                method: 'DELETE',
            });
            return { data: json };
        },

        getPersistedTestScenarios: async () => {
            const url = `${process.env.REACT_APP_INFRA_API}/admin/test-scenarios/persisted`;
            const { json } = await httpClient(url);
            return { data: json };
        },

        rotateJwtKeys: async (
            keys: Array<{
                application_guid: string;
                api_key: string;
                algorithm?: string;
                env?: string | null;
            }>
        ) => {
            const url = `${process.env.REACT_APP_INFRA_API}/admin/jwt-keys/rotate`;
            const { json } = await httpClient(url, {
                method: 'POST',
                body: JSON.stringify({ keys }),
            });
            return { data: json };
        },

        revokeJwtKey: async (kid: string) => {
            const url = `${process.env.REACT_APP_INFRA_API}/admin/jwt-keys/${encodeURIComponent(kid)}/revoke`;
            const { json } = await httpClient(url, {
                method: 'PUT',
            });
            return { data: json };
        },

        // OAuth Client application link management
        createOAuthClientApp: async (clientGuid: string, data: any) => {
            const url = `${process.env.REACT_APP_INFRA_API}/admin/oauth-clients/${encodeURIComponent(clientGuid)}/applications`;
            const { json } = await httpClient(url, {
                method: 'POST',
                body: JSON.stringify(data),
            });
            return { data: json };
        },

        updateOAuthClientApp: async (
            clientGuid: string,
            ocaGuid: string,
            data: any
        ) => {
            const url = `${process.env.REACT_APP_INFRA_API}/admin/oauth-clients/${encodeURIComponent(clientGuid)}/applications/${encodeURIComponent(ocaGuid)}`;
            const { json } = await httpClient(url, {
                method: 'PUT',
                body: JSON.stringify(data),
            });
            return { data: json };
        },

        deleteOAuthClientApp: async (clientGuid: string, ocaGuid: string) => {
            const url = `${process.env.REACT_APP_INFRA_API}/admin/oauth-clients/${encodeURIComponent(clientGuid)}/applications/${encodeURIComponent(ocaGuid)}`;
            const { json } = await httpClient(url, {
                method: 'DELETE',
            });
            return { data: json };
        },

        deleteOAuthClientPermanent: async (guid: string) => {
            const url = `${process.env.REACT_APP_INFRA_API}/admin/oauth-clients/${encodeURIComponent(guid)}?permanent=true`;
            const { json } = await httpClient(url, {
                method: 'DELETE',
            });
            return { data: json };
        },

        // Valkey key status (O(1) TTL check, no value transfer).
        // Redis TTL semantics: -2 = key does not exist, -1 = no expiry, > 0 = seconds remaining.
        getValkeyKeyStatus: async (key: string) => {
            const url = `${process.env.REACT_APP_INFRA_API}/valkey/${encodeURIComponent(key)}/status`;
            const { json } = await httpClient(url);
            return { data: json };
        },

        deleteValkeyKey: async (key: string) => {
            const url = `${process.env.REACT_APP_INFRA_API}/valkey/${encodeURIComponent(key)}`;
            const { json } = await httpClient(url, {
                method: 'DELETE',
            });
            return { data: json };
        },

        getInfoSchema: async (resource: string, type: string) => {
            const url = `${process.env.REACT_APP_INFRA_API}/admin/schemas/${encodeURIComponent(resource)}/${encodeURIComponent(type)}`;
            const { json } = await httpClient(url);
            return { data: json };
        },

        // Discontinue a continuous (jatkuva) user license, optionally targeting a specific order (tilausnumero).
        discontinueUserLicense: async (guid: string, tilausnumero?: string) => {
            const url = `${process.env.REACT_APP_INFRA_API}/userlicense/discontinue/${encodeURIComponent(guid)}`;
            const { json } = await httpClient(url, {
                method: 'PUT',
                body: JSON.stringify({ tilausnumero: tilausnumero ?? null }),
            });
            return { data: json };
        },

        // Restore a previously discontinued user license back to active.
        restoreUserLicense: async (guid: string) => {
            const url = `${process.env.REACT_APP_INFRA_API}/userlicense/restore/${encodeURIComponent(guid)}`;
            const { json } = await httpClient(url, {
                method: 'PUT',
            });
            return { data: json };
        },
    };
};

export default addInfraMethods;
